import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  FileText, 
  ArrowUpRight, 
  ArrowDownLeft,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Plus,
  X,
  Trash2
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useWorkspace } from '../hooks/useWorkspace'

interface FICDocument {
  id: string
  date: string
  description: string
  original_description?: string
  amount: number
  type: string
  source_type: string
  entity_name?: string
}

interface ManualInvoice {
  id: string
  type: 'emessa' | 'ricevuta'
  number?: string
  date: string
  due_date?: string
  counterparty_name?: string
  counterparty_vat?: string
  total_amount: number
  net_amount?: number
  vat_amount?: number
  currency: string
  payment_status: string
  paid_amount: number
  notes?: string
  source: string
}

interface DocumentsResponse {
  data: FICDocument[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
  totals: {
    invoices: { count: number; total: number }
    received: { count: number; total: number }
    cashbook: { count: number; total: number }
  }
  dateRange?: {
    from: string
    to: string
  }
  totalAllDocs?: number
}

type DataSource = 'manual' | 'fic'
type DocType = 'all' | 'invoice' | 'received' | 'cashbook'
type DateRange = '1y' | '2y' | '3y' | 'all'

const tabs: { id: DocType; label: string; icon: React.ReactNode }[] = [
  { id: 'all', label: 'Tutti', icon: <FileText className="w-4 h-4" /> },
  { id: 'invoice', label: 'Fatture Emesse', icon: <ArrowUpRight className="w-4 h-4" /> },
  { id: 'received', label: 'Fatture Ricevute', icon: <ArrowDownLeft className="w-4 h-4" /> },
  { id: 'cashbook', label: 'Prima Nota', icon: <FileText className="w-4 h-4" /> },
]

const dateRangeOptions: { id: DateRange; label: string }[] = [
  { id: '1y', label: 'Ultimo anno' },
  { id: '2y', label: 'Ultimi 2 anni' },
  { id: '3y', label: 'Ultimi 3 anni' },
  { id: 'all', label: 'Tutti' },
]

export default function Invoices() {
  const { language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const queryClient = useQueryClient()
  const [dataSource, setDataSource] = useState<DataSource>('manual')
  const [activeTab, setActiveTab] = useState<DocType>('all')
  const [page, setPage] = useState(1)
  const [dateRange, setDateRange] = useState<DateRange>('2y')
  const [showNewInvoice, setShowNewInvoice] = useState(false)
  const [newInvoice, setNewInvoice] = useState({
    type: 'ricevuta' as 'emessa' | 'ricevuta',
    number: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    counterparty_name: '',
    counterparty_vat: '',
    total_amount: '',
    net_amount: '',
    vat_amount: '',
    notes: '',
  })
  const perPage = 25

  // Calculate date_from based on selected range
  const dateFrom = useMemo(() => {
    if (dateRange === 'all') return '2000-01-01' // Effectively all documents
    const now = new Date()
    const years = dateRange === '1y' ? 1 : dateRange === '2y' ? 2 : 3
    const fromDate = new Date(now.getFullYear() - years, now.getMonth(), now.getDate())
    return fromDate.toISOString().split('T')[0]
  }, [dateRange])

  const { data, isLoading, refetch, isFetching } = useQuery<DocumentsResponse>({
    queryKey: ['fic-documents', activeTab, page, dateFrom],
    queryFn: async () => {
      const response = await api.get(`/integrations/fattureincloud/documents?type=${activeTab}&page=${page}&per_page=${perPage}&date_from=${dateFrom}`)
      return response.data
    },
    enabled: dataSource === 'fic',
  })

  // Manual invoices from D1 database
  const { data: manualInvoices, isLoading: manualLoading } = useQuery<{ invoices: ManualInvoice[]; total: number }>({
    queryKey: ['manual-invoices', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/invoices?pageSize=500')
      return response.data
    },
    enabled: dataSource === 'manual' && !!currentWorkspace?.id,
  })

  const createInvoiceMutation = useMutation({
    mutationFn: async (invoice: Record<string, unknown>) => {
      const response = await api.post('/invoices', invoice)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-stats'] })
      setShowNewInvoice(false)
      resetNewInvoice()
    },
  })

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/invoices/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manual-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-stats'] })
    },
  })

  const resetNewInvoice = () => {
    setNewInvoice({
      type: 'ricevuta',
      number: '',
      date: new Date().toISOString().split('T')[0],
      due_date: '',
      counterparty_name: '',
      counterparty_vat: '',
      total_amount: '',
      net_amount: '',
      vat_amount: '',
      notes: '',
    })
  }

  const handleCreateInvoice = () => {
    const totalAmount = parseFloat(newInvoice.total_amount.replace(',', '.'))
    if (isNaN(totalAmount) || totalAmount <= 0) return
    createInvoiceMutation.mutate({
      type: newInvoice.type,
      number: newInvoice.number || undefined,
      date: newInvoice.date,
      due_date: newInvoice.due_date || undefined,
      counterparty_name: newInvoice.counterparty_name || undefined,
      counterparty_vat: newInvoice.counterparty_vat || undefined,
      total_amount: totalAmount,
      net_amount: newInvoice.net_amount ? parseFloat(newInvoice.net_amount.replace(',', '.')) : undefined,
      vat_amount: newInvoice.vat_amount ? parseFloat(newInvoice.vat_amount.replace(',', '.')) : undefined,
      notes: newInvoice.notes || undefined,
    })
  }

  const filteredManualInvoices = useMemo(() => {
    if (!manualInvoices?.invoices) return []
    if (activeTab === 'all') return manualInvoices.invoices
    if (activeTab === 'invoice') return manualInvoices.invoices.filter(i => i.type === 'emessa')
    if (activeTab === 'received') return manualInvoices.invoices.filter(i => i.type === 'ricevuta')
    return manualInvoices.invoices
  }, [manualInvoices, activeTab])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'invoice':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
      case 'received':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'cashbook':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }

  const getTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'invoice':
        return 'Emessa'
      case 'received':
        return 'Ricevuta'
      case 'cashbook':
        return 'Prima Nota'
      default:
        return sourceType
    }
  }

  return (
    <div className="space-y-6 pt-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {language === 'it' ? 'Fatture' : 'Invoices'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            {language === 'it' ? 'Gestione fatture emesse e ricevute' : 'Manage issued and received invoices'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Data source toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
            <button
              onClick={() => setDataSource('manual')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                dataSource === 'manual'
                  ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {language === 'it' ? 'Manuali' : 'Manual'}
            </button>
            <button
              onClick={() => setDataSource('fic')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                dataSource === 'fic'
                  ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Fatture in Cloud
            </button>
          </div>

          {dataSource === 'manual' && (
            <button
              onClick={() => setShowNewInvoice(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              {language === 'it' ? 'Nuova fattura' : 'New invoice'}
            </button>
          )}

          {dataSource === 'fic' && (
            <>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <select
                  value={dateRange}
                  onChange={(e) => { setDateRange(e.target.value as DateRange); setPage(1) }}
                  className="text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {dateRangeOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                {language === 'it' ? 'Aggiorna' : 'Refresh'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* New Invoice Modal */}
      {showNewInvoice && (
        <div className="card border-2 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {language === 'it' ? 'Nuova Fattura' : 'New Invoice'}
            </h3>
            <button onClick={() => { setShowNewInvoice(false); resetNewInvoice() }} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Tipo' : 'Type'} *
              </label>
              <select
                value={newInvoice.type}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, type: e.target.value as 'emessa' | 'ricevuta' }))}
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              >
                <option value="ricevuta">{language === 'it' ? 'Fattura Ricevuta (costo)' : 'Received (expense)'}</option>
                <option value="emessa">{language === 'it' ? 'Fattura Emessa (ricavo)' : 'Issued (income)'}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Numero fattura' : 'Invoice number'}
              </label>
              <input
                type="text"
                value={newInvoice.number}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, number: e.target.value }))}
                placeholder="FT-001"
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Data' : 'Date'} *
              </label>
              <input
                type="date"
                value={newInvoice.date}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, date: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Controparte' : 'Counterparty'}
              </label>
              <input
                type="text"
                value={newInvoice.counterparty_name}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, counterparty_name: e.target.value }))}
                placeholder={language === 'it' ? 'Nome fornitore / cliente' : 'Supplier / client name'}
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">P.IVA</label>
              <input
                type="text"
                value={newInvoice.counterparty_vat}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, counterparty_vat: e.target.value }))}
                placeholder="IT01234567890"
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Scadenza' : 'Due date'}
              </label>
              <input
                type="date"
                value={newInvoice.due_date}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Importo totale (€)' : 'Total amount (€)'} *
              </label>
              <input
                type="text"
                value={newInvoice.total_amount}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, total_amount: e.target.value }))}
                placeholder="1.220,00"
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {language === 'it' ? 'Imponibile (€)' : 'Net amount (€)'}
              </label>
              <input
                type="text"
                value={newInvoice.net_amount}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, net_amount: e.target.value }))}
                placeholder="1.000,00"
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">IVA (€)</label>
              <input
                type="text"
                value={newInvoice.vat_amount}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, vat_amount: e.target.value }))}
                placeholder="220,00"
                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {language === 'it' ? 'Note' : 'Notes'}
            </label>
            <input
              type="text"
              value={newInvoice.notes}
              onChange={(e) => setNewInvoice(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={language === 'it' ? 'Note opzionali...' : 'Optional notes...'}
              className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <button
              onClick={() => { setShowNewInvoice(false); resetNewInvoice() }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </button>
            <button
              onClick={handleCreateInvoice}
              disabled={!newInvoice.date || !newInvoice.total_amount || createInvoiceMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {createInvoiceMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {language === 'it' ? 'Salva fattura' : 'Save invoice'}
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards — Manual invoices */}
      {dataSource === 'manual' && manualInvoices && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <ArrowUpRight className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{language === 'it' ? 'Fatture Emesse' : 'Issued'}</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {formatCurrency((manualInvoices.invoices || []).filter(i => i.type === 'emessa').reduce((s, i) => s + i.total_amount, 0))}
                </p>
                <p className="text-xs text-gray-500">{(manualInvoices.invoices || []).filter(i => i.type === 'emessa').length} {language === 'it' ? 'documenti' : 'documents'}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <ArrowDownLeft className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{language === 'it' ? 'Fatture Ricevute' : 'Received'}</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {formatCurrency((manualInvoices.invoices || []).filter(i => i.type === 'ricevuta').reduce((s, i) => s + i.total_amount, 0))}
                </p>
                <p className="text-xs text-gray-500">{(manualInvoices.invoices || []).filter(i => i.type === 'ricevuta').length} {language === 'it' ? 'documenti' : 'documents'}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{language === 'it' ? 'Totale' : 'Total'}</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {(manualInvoices.invoices || []).length}
                </p>
                <p className="text-xs text-gray-500">{language === 'it' ? 'fatture registrate' : 'invoices registered'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards — FIC */}
      {dataSource === 'fic' && data?.totals && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <ArrowUpRight className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Fatture Emesse</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {formatCurrency(data.totals.invoices.total)}
                </p>
                <p className="text-xs text-gray-500">{data.totals.invoices.count} documenti</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <ArrowDownLeft className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Fatture Ricevute</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {formatCurrency(data.totals.received.total)}
                </p>
                <p className="text-xs text-gray-500">{data.totals.received.count} documenti</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">Prima Nota</p>
                <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
                  {formatCurrency(data.totals.cashbook.total)}
                </p>
                <p className="text-xs text-gray-500">{data.totals.cashbook.count} movimenti</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-slate-700">
        <nav className="flex gap-4" aria-label="Tabs">
          {(dataSource === 'manual'
            ? [
                { id: 'all' as DocType, label: language === 'it' ? 'Tutte' : 'All', icon: <FileText className="w-4 h-4" /> },
                { id: 'invoice' as DocType, label: language === 'it' ? 'Emesse' : 'Issued', icon: <ArrowUpRight className="w-4 h-4" /> },
                { id: 'received' as DocType, label: language === 'it' ? 'Ricevute' : 'Received', icon: <ArrowDownLeft className="w-4 h-4" /> },
              ]
            : tabs
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1) }}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Manual Invoices Table */}
      {dataSource === 'manual' && (
        <div className="card overflow-hidden">
          {manualLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : !filteredManualInvoices.length ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <FileText className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-4" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {language === 'it' ? 'Nessuna fattura registrata' : 'No invoices registered'}
              </p>
              <p className="text-sm text-slate-400 max-w-[360px] text-center">
                {language === 'it'
                  ? 'Clicca "Nuova fattura" per registrare fatture emesse o ricevute. Saranno utilizzate per la riconciliazione bancaria.'
                  : 'Click "New invoice" to register issued or received invoices. They will be used for bank reconciliation.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {language === 'it' ? 'Data' : 'Date'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {language === 'it' ? 'Tipo' : 'Type'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {language === 'it' ? 'N. / Controparte' : 'No. / Counterparty'}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {language === 'it' ? 'Importo' : 'Amount'}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      {language === 'it' ? 'Stato' : 'Status'}
                    </th>
                    <th className="px-4 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredManualInvoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(inv.date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          inv.type === 'emessa'
                            ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
                            : 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
                        }`}>
                          {inv.type === 'emessa' ? (language === 'it' ? 'Emessa' : 'Issued') : (language === 'it' ? 'Ricevuta' : 'Received')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        <div className="max-w-xs truncate">
                          {inv.number && <span className="text-gray-500 dark:text-gray-400 mr-2">{inv.number}</span>}
                          {inv.counterparty_name || (language === 'it' ? 'Senza controparte' : 'No counterparty')}
                        </div>
                        {inv.notes && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 truncate">{inv.notes}</div>
                        )}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium text-right ${
                        inv.type === 'emessa' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {inv.type === 'emessa' ? '+' : '-'}{formatCurrency(inv.total_amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          inv.payment_status === 'paid'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : inv.payment_status === 'overdue'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {inv.payment_status === 'paid' ? (language === 'it' ? 'Pagata' : 'Paid')
                            : inv.payment_status === 'overdue' ? (language === 'it' ? 'Scaduta' : 'Overdue')
                            : (language === 'it' ? 'In attesa' : 'Pending')}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => deleteInvoiceMutation.mutate(inv.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* FIC Documents Table */}
      {dataSource === 'fic' && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : !data?.data?.length ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <FileText className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-4" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {language === 'it' ? 'Nessun documento trovato' : 'No documents found'}
              </p>
              <p className="text-sm text-slate-400 max-w-[360px] text-center">
                {language === 'it' ? 'Connetti Fatture in Cloud dalla pagina Integrazioni per sincronizzare i documenti' : 'Connect Fatture in Cloud from the Integrations page to sync documents'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {language === 'it' ? 'Data' : 'Date'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {language === 'it' ? 'Tipo' : 'Type'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {language === 'it' ? 'Descrizione' : 'Description'}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {language === 'it' ? 'Importo' : 'Amount'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                    {data.data.map((doc) => (
                      <tr key={doc.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(doc.date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(doc.source_type)}`}>
                            {getTypeLabel(doc.source_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          <div className="max-w-xs truncate" title={doc.description}>
                            {doc.description}
                          </div>
                          {doc.entity_name && doc.entity_name !== doc.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {doc.entity_name}
                            </div>
                          )}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium text-right ${
                          doc.amount >= 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {doc.amount >= 0 ? '+' : ''}{formatCurrency(doc.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {language === 'it' ? 'Pagina' : 'Page'} {data.pagination.page} {language === 'it' ? 'di' : 'of'} {data.pagination.total_pages} ({data.pagination.total} {language === 'it' ? 'totali' : 'total'})
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="btn-secondary p-2 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(data.pagination.total_pages, p + 1))}
                      disabled={page >= data.pagination.total_pages}
                      className="btn-secondary p-2 disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
